import PropTypes from 'prop-types';
import React from 'react';
import styled from 'styled-components';
import { buttonClicked } from 'web-ui-kit/buttons/events';
import { surfaceM, grayLightM, selectedLightHighlightM, selectedLightM, selectedHighlightM, successM, selectedMediumHighlightM, backgroundLightM, textDisabledM } from 'web-ui-kit/colors';
import { fontWeightNormal, BodyLargeStyles } from 'web-ui-kit/typography';

function _taggedTemplateLiteral(strings, raw) {
  if (!raw) {
    raw = strings.slice(0);
  }

  return Object.freeze(Object.defineProperties(strings, {
    raw: {
      value: Object.freeze(raw)
    }
  }));
}

var _templateObject, _templateObject2, _templateObject3, _templateObject4;
/**
 * When a user can select 1 or more options from a set.
 * Similar to a checkbox, but used when more screen space is available.
 *
 * The button width will expand to fit the label and text is wrapped.
 * The button height is fixed (see height prop).
 */


function UnsegmentedControl(_ref) {
  var _ref$className = _ref.className,
      className = _ref$className === void 0 ? '' : _ref$className,
      _ref$disabled = _ref.disabled,
      disabled = _ref$disabled === void 0 ? false : _ref$disabled,
      _ref$dispatch = _ref.dispatch,
      dispatch = _ref$dispatch === void 0 ? function () {} : _ref$dispatch,
      _ref$height = _ref.height,
      height = _ref$height === void 0 ? '5rem' : _ref$height,
      _ref$labelWeight = _ref.labelWeight,
      labelWeight = _ref$labelWeight === void 0 ? fontWeightNormal : _ref$labelWeight,
      _ref$Icon = _ref.Icon,
      Icon = _ref$Icon === void 0 ? null : _ref$Icon,
      _ref$id = _ref.id,
      id = _ref$id === void 0 ? 'unsegmented-control' : _ref$id,
      _ref$labelText = _ref.labelText,
      labelText = _ref$labelText === void 0 ? '' : _ref$labelText,
      _ref$subLabelText = _ref.subLabelText,
      subLabelText = _ref$subLabelText === void 0 ? '' : _ref$subLabelText,
      _ref$selected = _ref.selected,
      selected = _ref$selected === void 0 ? false : _ref$selected;

  var handleClick = function handleClick(jsEvent) {
    dispatch(buttonClicked({
      id: id,
      disabled: disabled,
      jsEvent: jsEvent,
      labelText: labelText,
      subLabelText: subLabelText,
      selected: selected
    }));
  };

  var Button = styles.Button,
      Content = styles.Content,
      Label = styles.Label,
      Sublabel = styles.Sublabel;
  return (
    /*#__PURE__*/
    React.createElement(Button, {
      className: className,
      height: height,
      isDisabled: disabled,
      onClick: handleClick,
      selected: selected,
      hasIcon: !!Icon
    },
    /*#__PURE__*/
    React.createElement(Content, null, Icon,
    /*#__PURE__*/
    React.createElement(Label, {
      labelWeight: labelWeight
    }, labelText),
    /*#__PURE__*/
    React.createElement(Sublabel, null, subLabelText)))
  );
}

UnsegmentedControl.propTypes = {
  /** Allows extending styles with styled(UnsegmentedControl) constructor */
  className: PropTypes.string,

  /** Whether or not the control is currently disabled */
  disabled: PropTypes.bool,

  /** For reporting events up to parent */
  dispatch: PropTypes.func,

  /** The fixed control height */
  height: PropTypes.string,

  /**
   * An optional button icon.
   * The icon should be sized to fit well in the button.
   */
  Icon: PropTypes.element,

  /**
   * An optional id string that can be used
   * to help identify the origin of click events.
   * This string is not user visible.
   */
  id: PropTypes.string,

  /** The unsegmented control for setting the font weight on the main label */
  labelWeight: PropTypes.number,

  /** The unsegmented control label text */
  labelText: PropTypes.string,

  /** The unsegmented control sub-label text */
  subLabelText: PropTypes.string,

  /** Whether or not the control is currently selected */
  selected: PropTypes.bool
};
var styles = {
  Button: styled.button(_templateObject || (_templateObject = _taggedTemplateLiteral(["\n    /** disable browser button styles */\n    appearance: none;\n    outline: none;\n\n    ", ";\n\n    background-color: ", ";\n    border: 1px solid ", ";\n    border-radius: 4px;\n    cursor: pointer;\n    height: ", ";\n    overflow: hidden;\n    padding: 0 0.75rem;\n    transition: all 250ms ease-in-out;\n\n    ", "\n\n    ", "\n\n    ", "\n\n    ", "\n  "])), BodyLargeStyles, surfaceM, grayLightM, function (p) {
    return p.height;
  }, function (p) {
    return (!p.isDisabled || p.hasIcon) && "\n      @media (hover: hover) {\n        &:hover {\n          background-color: ".concat(selectedLightHighlightM, ";\n          border-color: ").concat(selectedLightM, ";\n        }\n      }\n    ");
  }, function (p) {
    return (!p.isDisabled || p.hasIcon) && p.selected && "\n      background-color: ".concat(selectedHighlightM, ";\n      border: 2px solid ").concat(successM, ";\n\n      @media (hover: hover) {\n        &:hover {\n          background-color: ").concat(selectedMediumHighlightM, ";\n          border-color: ").concat(successM, ";\n        }\n      }\n    ");
  }, function (p) {
    return p.isDisabled && !p.hasIcon && "\n      background-color: ".concat(backgroundLightM, ";\n      color: ").concat(textDisabledM, ";\n      cursor: auto;\n    ");
  }, function (p) {
    return p.isDisabled && p.selected && "\n      border: 2px solid ".concat(successM, ";\n    ");
  }),
  Content: styled.div(_templateObject2 || (_templateObject2 = _taggedTemplateLiteral(["\n    display: flex;\n    flex-direction: column;\n    justify-content: center;\n    align-items: center;\n  "]))),
  Label: styled.p(_templateObject3 || (_templateObject3 = _taggedTemplateLiteral(["\n    margin: 0;\n    font-weight: ", ";\n  "])), function (p) {
    return p.labelWeight;
  }),
  Sublabel: styled.p(_templateObject4 || (_templateObject4 = _taggedTemplateLiteral(["\n    margin: 0;\n  "])))
};
export default UnsegmentedControl;